/**
 * Small unit-of-measure selector with per-user MRU (#2672).
 * Values are domain identifiers (piece, liter, …); labels come from common.units.
 */

import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { DROPDOWN_MRU_SELECTORS, useDropdownMru } from '@/hooks/useDropdownMru';

export const WORK_UNITS = ['piece', 'hour', 'liter', 'kg', 'meter', 'set', 'pair', 'box'] as const;

export interface UnitSelectProps {
  value: string | null;
  onChange: (unit: string) => void;
  label: string;
  size?: 'small' | 'medium';
}

export function UnitSelect({ value, onChange, label, size = 'small' }: UnitSelectProps) {
  const { t: tc } = useTranslation('common');
  const labelId = useId();
  const { order, record } = useDropdownMru(DROPDOWN_MRU_SELECTORS.unit);
  const units = useMemo(() => {
    const catalog: string[] = [...WORK_UNITS];
    if (value && !catalog.includes(value)) catalog.push(value);
    return order(catalog, (unit) => unit);
  }, [order, value]);

  return (
    <FormControl size={size} sx={{ minWidth: 96 }}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        value={value ?? ''}
        label={label}
        onChange={(event) => {
          const next = event.target.value;
          onChange(next);
          if (next !== '') record(next);
        }}
      >
        {value ? null : <MenuItem value="">{'\u00a0'}</MenuItem>}
        {units.map((unit) => (
          <MenuItem key={unit} value={unit}>
            {tc(`units.${unit}`, unit)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
